import React, { useCallback, useMemo } from 'react';
import { LC_THEME_KEY, THEME_BLUE, THEME_LIGHT } from './utils';
import { useLocalStorage } from '../utils/useLocalStorage';
import IconLightMode from '@mui/icons-material/LightMode';
import IconDarkMode from '@mui/icons-material/DarkMode';
import Box from '@mui/material/Box';
import { blueTheme } from './blueTheme';
import { coreTheme } from './coreTheme';
import { createTheme, ThemeProvider } from '@mui/material';
import { deDE as coreDeDE, enUS as coreEnUS } from '@mui/material/locale';
import { deDE as gridDeDE, enUS as gridEnUS } from '@mui/x-data-grid/locales';
import { deDE as pickersDeDE, enUS as pickersEnUS } from '@mui/x-date-pickers/locales';
import { LocalizationProvider as DateLocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de, enUS } from 'date-fns/locale';
import { useLocalizationContext } from '../localization/LocalizationContext';
import { getShortLanguageCode } from '../utils/locale';
import { colors } from '../utils/colors';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

export const MuiThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const [theme, setTheme] = useLocalStorage<string>(LC_THEME_KEY, THEME_LIGHT);
    const { currentLocale } = useLocalizationContext();
    const isGerman = getShortLanguageCode(currentLocale) === 'de';
    const activeTheme = useMemo(
        () =>
            createTheme(
                theme === THEME_BLUE ? blueTheme : coreTheme,
                isGerman ? coreDeDE : coreEnUS,
                isGerman ? gridDeDE : gridEnUS,
                isGerman ? pickersDeDE : pickersEnUS
            ),
        [theme, isGerman]
    );
    const toggleTheme = useCallback(() => setTheme(theme === THEME_LIGHT ? THEME_BLUE : THEME_LIGHT), [theme]);
    const btnStyle = useMemo(
        () => ({
            position: 'absolute',
            zIndex: 100001,
            right: '2px',
            top: '2px',
            cursor: 'pointer',
            svg: {
                width: '14px',
                height: '14px',
                fill: theme === THEME_BLUE ? colors.gray.wild_sand : colors.blue.madison
            }
        }),
        [theme]
    );

    return (
        <ThemeProvider theme={activeTheme}>
            <StyledThemeProvider theme={activeTheme}>
                <Box sx={btnStyle} onClick={toggleTheme}>
                    {theme === THEME_LIGHT ? <IconDarkMode /> : <IconLightMode />}
                </Box>

                <DateLocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isGerman ? de : enUS}>
                    {children}
                </DateLocalizationProvider>
            </StyledThemeProvider>
        </ThemeProvider>
    );
};
